import React, {
  ReactNode,
  useState,
  useEffect,
  createContext,
  useMemo,
  useCallback,
  useContext,
} from 'react';
import Polyglot from 'node-polyglot';
import { merge } from 'lodash';

import { useConfig } from '../../hooks/queries/useConfig';
import { useLocalization } from '../../hooks/queries/useLocalization';

import * as mainTranslations from './translations';

import {
  SupportedLanguage,
  getBrowserLanguage,
  getStoredLanguage,
  storeLanguage,
} from './languageUtils';
import { LocalizationBundle } from 'types/localizationBundle.types';

type TranslationMessages = Record<string, unknown>;

const mergeTranslations = (
  main: TranslationMessages,
  overrides: TranslationMessages
): TranslationMessages => {
  return merge({}, main, overrides);
};

const createTranslationsByLanguage = (
  localizationBundles: LocalizationBundle[] = []
): Record<SupportedLanguage, TranslationMessages> => {
  const bundlesMap = new Map<string, TranslationMessages>();

  localizationBundles.forEach((bundle) => {
    bundlesMap.set(bundle.lang, bundle.bundle as TranslationMessages);
  });

  const getOverride = (lang: SupportedLanguage): TranslationMessages => bundlesMap.get(lang) || {};

  // Merge static main translations with possible API overrides per language
  return {
    en: mergeTranslations(mainTranslations.en as TranslationMessages, getOverride('en')),
    fi: mergeTranslations(mainTranslations.fi as TranslationMessages, getOverride('fi')),
    sv: mergeTranslations(mainTranslations.sv as TranslationMessages, getOverride('sv')),
  };
};

// Create a context for our custom i18n implementation
interface I18nContextValue {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string, options?: Polyglot.InterpolationOptions) => string;
}

export const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export const useI18nContext = (): I18nContextValue => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18nContext must be used within an I18nProvider');
  }
  return context;
};

interface I18nProviderProps {
  children: ReactNode;
}

export const DEFAULT_LANGUAGE: SupportedLanguage = 'fi';

// Define map for custom demo parameters to languages
const demoParamToLanguageMap: Record<string, string> = {
  'riku-en-demo': 'en',
  // Original UUID mappings
  '93a599b2-2371-477f-8537-0e00f0876fcf': 'en',
  '6e1ea7f7-21b5-4e87-a51d-18d45d101be6': 'sv',
  // More mappings can be added here
};

const determineLanguageFromUrl = (supportedLanguages: string[]): SupportedLanguage => {
  const searchParams = new URLSearchParams(window.location.search);
  const langParam = searchParams.get('lang') ?? 'fi';
  const overrideLanguageParam = searchParams.get('demo') ?? '';

  const mappedLanguage = demoParamToLanguageMap[overrideLanguageParam];

  const urlLanguage = mappedLanguage
    ? mappedLanguage
    : langParam && supportedLanguages.includes(langParam)
      ? langParam
      : 'fi';

  return urlLanguage as SupportedLanguage;
};

export const I18nProvider = ({ children }: I18nProviderProps): React.ReactElement => {
  const { data: config } = useConfig();
  const { data: localizationBundles } = useLocalization();

  const supportedLanguages: SupportedLanguage[] = (config?.languages as SupportedLanguage[]) || [
    'en',
    'fi',
    'sv',
  ];

  const hasUrlLanguageParams = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const langParam = searchParams.get('lang');
    const demoParam = searchParams.get('demo');
    return !!(langParam || (demoParam && demoParamToLanguageMap[demoParam]));
  }, []);

  const [hasManualSelection, setHasManualSelection] = useState(false);

  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    if (!hasUrlLanguageParams) {
      return getStoredLanguage() || DEFAULT_LANGUAGE;
    }

    return DEFAULT_LANGUAGE;
  });

  useEffect(() => {
    if (hasManualSelection) return;
    if (!config?.languages) return;

    // Config-level locale override (rename this field if your config uses a different name)
    const configLocale = config?.localeOverride;

    // 1) Config locale override wins if valid
    if (configLocale && supportedLanguages.includes(configLocale)) {
      if (language !== configLocale) {
        setLanguageState(configLocale);
      }
      return;
    }

    // 2) URL-based language if present
    if (hasUrlLanguageParams) {
      const urlLanguage = determineLanguageFromUrl(config.languages);
      if (urlLanguage !== language) {
        setLanguageState(urlLanguage);
      }
      return;
    }

    // 3) Stored language if we’re still at default
    if (language === DEFAULT_LANGUAGE) {
      const storedLang = getStoredLanguage(config.languages);
      if (storedLang && storedLang !== language) {
        setLanguageState(storedLang);
        return;
      }

      const browserLang = getBrowserLanguage(config.languages);
      if (browserLang && browserLang !== language) {
        setLanguageState(browserLang);
      }
    }
  }, [
    config?.languages,
    config,
    language,
    hasUrlLanguageParams,
    hasManualSelection,
    supportedLanguages,
  ]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const polyglot = useMemo(() => {
    const translationsByLanguage = createTranslationsByLanguage(localizationBundles ?? []);
    const messages = translationsByLanguage[language];

    const instance = new Polyglot({
      locale: language,
      phrases: messages,
      allowMissing: true,
      onMissingKey: (key) => `[${key}]`,
      interpolation: { prefix: '{', suffix: '}' },
    });

    return instance;
  }, [language, localizationBundles]);

  const t = useCallback(
    (key: string, options?: Polyglot.InterpolationOptions): string => {
      return polyglot.t(key, options);
    },
    [polyglot]
  );

  const setLanguage = useCallback(
    (newLanguage: SupportedLanguage) => {
      if (supportedLanguages.includes(newLanguage)) {
        setLanguageState(newLanguage);
        setHasManualSelection(true);
        try {
          storeLanguage(newLanguage);
        } catch (error) {
          console.warn('Could not store language preference:', error);
        }
      }
    },
    [supportedLanguages]
  );

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
    }),
    [language, setLanguage, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};
